import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static values = {
    pollUrl: String,
    cacheUrl: String,
    csrf: String
  }

  static targets = ["tbody", "summary", "button", "status", "empty", "table"]

  connect() {
    this.pollInterval = null
  }

  disconnect() {
    this.stopPolling()
  }

  async start(event) {
    event.preventDefault()
    if (this.hasButtonTarget) this.buttonTarget.disabled = true
    this.setStatus("Queuing cache jobs…")

    try {
      const response = await fetch(this.cacheUrlValue, {
        method: "POST",
        headers: {
          "X-CSRF-Token": this.csrfValue,
          Accept: "application/json",
          "Content-Type": "application/json",
          "X-Requested-With": "XMLHttpRequest"
        },
        credentials: "same-origin"
      })
      if (!response.ok) throw new Error(`HTTP ${response.status}`)
      const data = await response.json()
      this.applyPayload(data)
      const queued = data.enqueued || 0
      if (queued > 0) {
        this.setStatus(`Queued ${queued} cache job${queued === 1 ? "" : "s"}. This table updates as they finish.`)
        this.schedulePoll()
      } else {
        this.setStatus("Nothing to cache.")
        if (this.hasButtonTarget) this.buttonTarget.disabled = true
      }
    } catch (error) {
      console.error("Failed to queue mask caches", error)
      this.setStatus("Could not queue cache jobs.")
      if (this.hasButtonTarget) this.buttonTarget.disabled = false
    }
  }

  schedulePoll() {
    if (this.pollInterval) return
    this.pollInterval = setInterval(() => this.poll(), 3000)
  }

  stopPolling() {
    if (this.pollInterval) {
      clearInterval(this.pollInterval)
      this.pollInterval = null
    }
  }

  async poll() {
    if (!this.pollUrlValue) return
    try {
      const response = await fetch(this.pollUrlValue, {
        headers: { Accept: "application/json" },
        credentials: "same-origin"
      })
      if (!response.ok) return
      const data = await response.json()
      this.applyPayload(data)
      if (!data.missing || data.missing.length === 0) {
        this.setStatus("All layer+CQL extents now have a mask cache.")
        this.stopPolling()
      }
    } catch (_error) {
      // retry on the next interval
    }
  }

  applyPayload(data) {
    const missing = data.missing || []
    const missingIds = new Set(missing.map((item) => String(item.id)))
    this.updateSummary(data)
    this.updateUncachedTable(missing)
    this.updateMainTableBadges(missingIds)
    this.updateTabBadge(missing.length)
    if (this.hasButtonTarget) this.buttonTarget.disabled = missing.length === 0
  }

  updateSummary(data) {
    if (!this.hasSummaryTarget) return
    const cacheable = data.cacheable || 0
    const cached = data.cached || 0
    const missing = (data.missing || []).length
    this.summaryTarget.textContent = `${missing} uncached of ${cacheable} layer+CQL extents (${cached} cached)`
  }

  updateTabBadge(missingCount) {
    const badge = document.getElementById("uncachedExtentsTabBadge")
    if (!badge) return
    badge.textContent = `${missingCount} uncached`
    badge.classList.toggle("d-none", missingCount === 0)
  }

  updateUncachedTable(missing) {
    if (this.hasEmptyTarget) this.emptyTarget.classList.toggle("d-none", missing.length > 0)
    if (this.hasTableTarget) this.tableTarget.classList.toggle("d-none", missing.length === 0)
    if (!this.hasTbodyTarget) return

    if (missing.length === 0) {
      this.tbodyTarget.innerHTML = ""
      return
    }

    this.tbodyTarget.innerHTML = missing.map((extent) => {
      const typeBadge = extent.type === "System"
        ? '<span class="badge badge-info">System</span>'
        : '<span class="badge badge-secondary">Team</span>'
      return `<tr data-uncached-id="${this.escapeAttr(String(extent.id))}">
        <td>${this.escapeHtml(String(extent.id))}</td>
        <td>${this.escapeHtml(extent.name || "")}</td>
        <td>${typeBadge}</td>
        <td>${this.escapeHtml(extent.region || "—")}</td>
        <td><code class="small">${this.escapeHtml(extent.layer || "")}</code></td>
        <td><code class="small" title="${this.escapeAttr(extent.cql || "")}">${this.escapeHtml(this.truncate(extent.cql || "", 48))}</code></td>
      </tr>`
    }).join("")
  }

  updateMainTableBadges(missingIds) {
    this.element.querySelectorAll("#extentsTable [data-extent-id][data-cacheable='true']").forEach((row) => {
      const badge = row.querySelector("[data-role='mask-cache-badge']")
      if (!badge) return
      const uncached = missingIds.has(row.getAttribute("data-extent-id"))
      badge.className = `badge ${uncached ? "badge-warning" : "badge-success"}`
      badge.textContent = uncached ? "Uncached" : "Cached"
    })
  }

  setStatus(text) {
    if (this.hasStatusTarget) this.statusTarget.textContent = text
  }

  truncate(text, length) {
    if (text.length <= length) return text
    return `${text.slice(0, length - 1)}…`
  }

  escapeHtml(text) {
    const div = document.createElement("div")
    div.textContent = text
    return div.innerHTML
  }

  escapeAttr(text) {
    return this.escapeHtml(text).replace(/"/g, "&quot;")
  }
}
